/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { AppContext } from 'store/context'
import ContractEntity from 'utils/__interfaces/Perimeter/ContractEntity'
import { distinctArray } from 'utils/Common/array-utils'
import isCancel from 'utils/Common/api-utils'
import { getNoDataMessage } from 'utils/Common/TableUtils'
import {
  TAB_CONTRACTS,
  TABLE_DEFAULT_RESULT_PER_PAGE,
} from 'utils/__consts/Common/Table'
import { filterBuilder } from 'utils/__interfaces/Filters/FilterBuilderInterface'
import EnumFilterType from 'utils/__enums/Filters/FilterBuilder'
import SvgIcon from 'components/Icons/SvgIcon'
import Table from 'components/Tables/Table'
import FilterBuilder from 'components/Filters/FilterBuilder'
import { getContratsForTable } from 'services/contract-services'

interface Props {
  onContratSelected: (ids: string[]) => void
  onDataContratSelected: (data: ContractEntity[]) => void
  contratItemsDatas: ContractEntity[]
  finalKeptIds: string[]
  setFinalList: React.Dispatch<React.SetStateAction<string[]>>
}

function WasteRegisterSelector({
  onContratSelected,
  onDataContratSelected,
  contratItemsDatas = [],
  finalKeptIds,
  setFinalList,
}: Props) {
  const { t } = useTranslation()
  const {
    state: { user, contract, organization },
  } = useContext(AppContext)
  const history = useHistory()
  // STATE
  const [isTimeOut, setIsTimeOut] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [tableDatas, setTableDatas] = useState<any[]>([])
  const [isResetSelection, setIsResetSelection] = useState(false)
  const [resultList, setResultList] = useState<string[]>([])
  const [contracts, setContracts] = React.useState<ContractEntity[]>([])
  const [nbPage, setNbPage] = useState<number>(0)
  const [tablePageCount, setTablePageCount] = useState(0)
  const [filters, setFilters] = React.useState<{ [key: string]: string[] }>({})
  const [resetPage, setResetPage] = useState(false)
  const [nbPerPage, setNbPerPage] = useState(TABLE_DEFAULT_RESULT_PER_PAGE)
  const itemsSelectedLabel = `${
    resultList.length <= 1
      ? t('generic.selected.singular')
      : t('generic.selected.plural')
  }`

  // Headers
  const CONTRATS_TABLE = [
    {
      Header: t('wasteRegister.table.name'),
      accessor: 'name',
    },
    {
      Header: t('wasteRegister.table.address'),
      accessor: 'address',
    },
    {
      Header: t('wasteRegister.table.city'),
      accessor: 'city',
    },
    {
      Header: t('wasteRegister.table.number'),
      accessor: 'number',
    },
  ]

  // HANDLER
  const checkAll = (itemIds: string[]) => {
    const unchecked: string[] = []

    itemIds.forEach((contractId: string) => {
      if (!resultList.includes(contractId)) {
        unchecked.push(contractId)
      }
    })

    if (unchecked.length > 0) {
      // add the previously unselected contracts to the list of selected contracts in this component and in the parent
      const resultingList = finalKeptIds.concat(unchecked)
      setResultList([...resultingList])
      onContratSelected(distinctArray([...resultingList]))

      // update the list of contracts to be displayed in the generator component
      const missingListData = contracts.filter((contract: ContractEntity) =>
        unchecked.includes(contract.id)
      )
      const newContractData = contratItemsDatas.concat(missingListData)

      onDataContratSelected(newContractData)
    } else {
      // remove all contracts displayed in the current selector page from the list of selected contracts
      const resultingListWhenAllChecked = finalKeptIds.filter(
        (selected) => !itemIds.includes(selected)
      )

      setResultList([...resultingListWhenAllChecked])
      onContratSelected(distinctArray([...resultingListWhenAllChecked]))

      // update the list of contracts to be displayed in the generator component
      const filteredListWithRemove = contratItemsDatas.filter(
        (contract: ContractEntity) =>
          resultingListWhenAllChecked.includes(contract.id)
      )

      onDataContratSelected(filteredListWithRemove)
    }
  }

  const checkOne = (itemId: string) => {
    let newResultList: string[] = []
    if (resultList.length === 0) {
      newResultList.push(itemId)
    } else {
      newResultList = resultList.includes(itemId)
        ? resultList.filter((id: string) => id !== itemId)
        : resultList.concat([itemId])
    }

    setResultList([...distinctArray(newResultList)])
    onContratSelected(distinctArray([...newResultList]))

    contracts.forEach((elem: ContractEntity) => {
      if (!contratItemsDatas.includes(elem)) contratItemsDatas.push(elem)
    })
    onDataContratSelected(contratItemsDatas)
  }

  const formateContrats = (contracts: ContractEntity[]) => {
    return contracts.map((item) => {
      return {
        id: item.id,
        name: <div className="wasteRegister__raisonSociale">{item.name}</div>,
        address: (
          <div className="wasteRegister__searchAdresse">{item.shortAdress}</div>
        ),
        city: <div className="wasteRegister__searchVille ">{item.villeCP}</div>,
        number: (
          <div className="wasteRegister__numClient">
            {item.numeroContratSAP}
          </div>
        ),
      }
    })
  }
  const loadContratsAsync = async () => {
    setIsLoading(true)
    const { results, pageCount, isTimeOut } = await getContratsForTable(
      nbPerPage,
      nbPage,
      organization.items,
      contract.items,
      filters,
      history
    )
    setIsTimeOut(isTimeOut)

    if (isCancel(results)) {
      return
    }
    setTablePageCount(pageCount)
    if (results) {
      setContracts(results)
      setTableDatas(formateContrats(results))
      setIsLoading(false)
    }
    setResetPage(false)
  }

  useEffect(() => {
    if (
      user.roleNames.length === 0 ||
      organization.items === undefined ||
      contract.items === undefined
    )
      return

    loadContratsAsync()
    setIsResetSelection(true)
  }, [user, nbPage, nbPerPage, filters])

  const applyFilters = (filters: { [key: string]: any | any[] }) => {
    setFilters(filters)
    setResetPage(true)
  }

  const resetFilters = () => {
    setFilters({})
    setResetPage(true)
  }

  useEffect(() => {
    resetFilters()
    setTableDatas([])
    setIsResetSelection(true)
    setResetPage(true)
    setFinalList([])
  }, [contract, organization])

  useEffect(() => {
    setResultList(() => finalKeptIds)
  }, [finalKeptIds])

  const filterBuilder: filterBuilder = {
    dynamic: [
      {
        name: 'entreprise',
        type: EnumFilterType.INPUT_STRING,
        label: 'Nom entreprise',
        placeholder: t('wasteRegister.filters.companyName'),
        apiName: 'searchText',
        multiple: true,
      },
      {
        name: 'adresse',
        type: EnumFilterType.INPUT_STRING,
        label: 'Adresse',
        placeholder: t('wasteRegister.filters.address'),
        apiName: 'searchAdresse',
        multiple: true,
      },
      {
        name: 'ville',
        type: EnumFilterType.INPUT_STRING,
        label: 'Ville, CP',
        placeholder: t('wasteRegister.filters.city'),
        apiName: 'searchVille',
        multiple: true,
      },
      {
        name: 'client',
        type: EnumFilterType.INPUT_STRING,
        label: 'Numéro',
        placeholder: t('wasteRegister.filters.client'),
        apiName: 'numeroContratSAP',
        multiple: true,
      },
    ],
    static: [],
  }

  return (
    <>
      <div className="wasteRegister__left">
        <div className="wasteRegister__header_selector">
          <SvgIcon name="building" className="wasteRegister_buttonicon" />
          <div className="title">{t('wasteRegister.leftTitle')}</div>
        </div>
        <div>
          <FilterBuilder
            onSubmitFilter={(filters: { [key: string]: any | any[] }) =>
              applyFilters(filters)
            }
            onResetFilter={() => resetFilters()}
            filterBuilder={filterBuilder}
            className=""
          />
        </div>
        <div>
          <div className="wasteRegister_selectedItemsCount">
            <p>
              {finalKeptIds.length} {itemsSelectedLabel}
            </p>
          </div>
        </div>
        <Table
          columns={CONTRATS_TABLE}
          data={tableDatas}
          checkedList={finalKeptIds}
          disabledColumnActionsList={['Numéro']}
          onSelectedItemChanged={checkOne}
          onSelectionChange={null}
          onCheckAll={checkAll}
          withFilters={false}
          isLoading={isLoading}
          isInternalPagination
          nbPerPage={nbPerPage}
          pageCount={tablePageCount}
          onPageChange={setNbPage}
          onNbPerPageChange={setNbPerPage}
          resetOrgaCtr={resetPage}
          forceReset={isResetSelection}
          noDataAvailable={getNoDataMessage(TAB_CONTRACTS)}
          isTimeOut={isTimeOut}
        />
      </div>
    </>
  )
}

export default WasteRegisterSelector
